import { useState, useMemo, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { MapContainer, TileLayer, Marker, Popup, ZoomControl, useMap, useMapEvents } from "react-leaflet";
import MarkerClusterGroup from "react-leaflet-cluster";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { MAP_POIS, MAP_W, MAP_H } from "@/data/mapPois";
import { CUSTOM_TYPES } from "@/data/customTypes";
import { ElementBadges } from "@/components/PalAvatar";
import { RankBadge } from "@/components/RankBadge";
import { PalDetailProvider, usePalDetail } from "@/components/PalDetailModal";
import { PALS_BY_INDEX } from "@/lib/breeding";
import { nextRank } from "@/data/ranks";
import { toast } from "sonner";
import { Skull, Castle, DoorOpen, Navigation, Apple, Gem, Filter, Search, MapPin, Plus, Flag, Trash2, X, Share2, Copy, Check, Eye, EyeOff, ListChecks, CheckCircle2, Circle, ChevronDown, ChevronRight, RotateCcw, StickyNote, Info } from "lucide-react";

const W = MAP_W;
const H = MAP_H;
// High-res tiled base map from TH.GL (4096²+ at native zoom, no blur at deep zoom).
// Their tile bounds match our POI world-extent exactly, so we only rescale by S.
const TILE_URL = "https://cdn.th.gl/palworld/map-tiles/default-733001e0986faa3f88b0a970412d7fb9/{z}/{y}/{x}.webp?v=1";
const TILE_SIZE = 512;
const MAX_NATIVE_ZOOM = 4;
const NATIVE = TILE_SIZE; // map size (px) at zoom 0 = one tile
const S = NATIVE / W; // scale from our 0..1000 pixel space -> tile coord space
const BOUNDS = [[0, 0], [NATIVE, NATIVE]];
// Non-flipped Simple CRS so lat increases downward, matching XYZ tile rows.
const TILE_CRS = L.Util.extend({}, L.CRS.Simple, { transformation: new L.Transformation(1, 0, 1, 0) });
// (x,y) in our 0..1000 space (y from top) -> Leaflet [lat,lng]
const LL = (x, y) => [y * S, x * S];
const fromLL = (latlng) => ({ x: latlng.lng / S, y: latlng.lat / S });

// palworld.gg-style POI categories.
export const POI_CATS = {
  fasttravel: { label: "Voyage rapide", color: "#38BDF8", icon: Navigation },
  tower: { label: "Tour du Syndicat", color: "#D946EF", icon: Castle },
  alpha: { label: "Pal Alpha", color: "#EF4444", icon: Skull },
  dungeon: { label: "Donjon", color: "#8B5CF6", icon: DoorOpen },
  skillfruit: { label: "Fruit de compétence", color: "#10B981", icon: Apple },
  sealed: { label: "Royaume scellé", color: "#F59E0B", icon: Gem },
  note: { label: "Note", color: "#94A3B8", icon: StickyNote },
};

const dotIcon = (color, size = 15) =>
  L.divIcon({
    className: "",
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
    html: `<div style="width:${size}px;height:${size}px;border-radius:9999px;background:${color};border:2px solid rgba(0,0,0,.55);box-shadow:0 0 7px ${color}"></div>`,
  });
const dotIconVisited = (size = 15) =>
  L.divIcon({
    className: "",
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
    html: `<div style="position:relative;width:${size}px;height:${size}px;border-radius:9999px;background:#64748b;border:2px solid rgba(0,0,0,.55);opacity:.55"><span style="position:absolute;top:-6px;right:-6px;background:#22C55E;color:#0b0f19;border-radius:9999px;width:11px;height:11px;font-size:8px;line-height:11px;text-align:center;font-weight:900;">✓</span></div>`,
  });

// Pal-face marker for alphas & sealed realm bosses (matches palworld.gg look).
const palIcon = (imgUrl, ring, defeated) =>
  L.divIcon({
    className: "",
    iconSize: [40, 40],
    iconAnchor: [20, 20],
    html: `<div style="position:relative;width:40px;height:40px;border-radius:9999px;overflow:visible;opacity:${defeated ? 0.45 : 1}">
      <img src="${imgUrl}" style="width:40px;height:40px;border-radius:9999px;object-fit:cover;background:#0b0f19;border:2.5px solid ${defeated ? "#22C55E" : ring};box-shadow:0 0 8px ${ring}99" />
      ${defeated ? `<span style="position:absolute;top:-4px;right:-4px;background:#22C55E;color:#0b0f19;border-radius:9999px;width:16px;height:16px;font-size:10px;line-height:16px;text-align:center;font-weight:900;">✓</span>` : ""}
    </div>`,
  });

const customIcon = (color, letter, visited) =>
  L.divIcon({
    className: "",
    iconSize: [30, 30],
    iconAnchor: [15, 15],
    html: `<div class="pal-marker" style="position:relative;width:30px;height:30px;background:${color}22;border-color:${visited ? "#22C55E" : color};color:${color};box-shadow:0 0 10px ${color}88;opacity:${visited ? 0.5 : 1};"><span style="font-size:13px;font-weight:800;">${letter}</span>${visited ? `<span style="position:absolute;top:-5px;right:-5px;background:#22C55E;color:#0b0f19;border-radius:9999px;width:15px;height:15px;font-size:10px;line-height:15px;text-align:center;font-weight:900;">✓</span>` : ""}</div>`,
  });

const poiKey = (p) => `${p.c}:${Math.round(p.x)}:${Math.round(p.y)}`;
const ringOf = (pal) => (pal?.rarity >= 10 ? "#FACC15" : pal?.rarity >= 8 ? "#D946EF" : pal?.rarity >= 5 ? "#3B82F6" : "#EF4444");

function MapController({ flyTarget, addMode, onMapClick, onView }) {
  const map = useMap();
  useEffect(() => {
    if (flyTarget) map.flyTo(LL(flyTarget.x, flyTarget.y), flyTarget.zoom ?? 3, { duration: 0.8 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flyTarget?.nonce]);
  useMapEvents({
    click(e) { if (addMode) onMapClick(e.latlng); },
    contextmenu(e) { onMapClick(e.latlng); },
    moveend(e) { onView && onView(e.target); },
    zoomend(e) { onView && onView(e.target); },
  });
  return null;
}

export default function MapPage() {
  const qc = useQueryClient();
  const openPal = usePalDetail();
  const VISIT_KEY = "palhub_visited_pois";
  const [visited, setVisited] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem(VISIT_KEY) || "[]")); } catch { return new Set(); }
  });
  const toggleVisited = (key) => setVisited((prev) => {
    const n = new Set(prev);
    if (n.has(key)) n.delete(key); else n.add(key);
    localStorage.setItem(VISIT_KEY, JSON.stringify([...n]));
    return n;
  });

  const initView = useMemo(() => {
    const sp = new URLSearchParams(window.location.search);
    const x = parseFloat(sp.get("x")), y = parseFloat(sp.get("y")), z = parseFloat(sp.get("z"));
    if (!isNaN(x) && !isNaN(y)) return { center: LL(x, y), zoom: isNaN(z) ? 1 : z };
    return null;
  }, []);
  const onView = (map) => {
    const c = map.getCenter();
    const { x, y } = fromLL(c);
    const z = map.getZoom();
    const sp = new URLSearchParams(window.location.search);
    sp.set("x", Math.round(x)); sp.set("y", Math.round(y)); sp.set("z", Number(z).toFixed(1));
    sp.delete("focus");
    window.history.replaceState(null, "", `${window.location.pathname}?${sp.toString()}`);
  };

  const pois = MAP_POIS;
  const [activePOI, setActivePOI] = useState(["fasttravel", "tower", "alpha", "dungeon", "skillfruit", "sealed"]);
  const togglePoi = (k) => setActivePOI((p) => (p.includes(k) ? p.filter((x) => x !== k) : [...p, k]));

  const [active, setActive] = useState(["custom"]);
  const [panelOpen, setPanelOpen] = useState(() => (typeof window !== "undefined" ? window.innerWidth >= 768 : false));
  const [query, setQuery] = useState("");
  const [flyTarget, setFlyTarget] = useState(null);
  const [addMode, setAddMode] = useState(false);
  const [draft, setDraft] = useState(null);
  const [markersVisible, setMarkersVisible] = useState(true);
  const [legendOpen, setLegendOpen] = useState(false);
  const [maxLevel, setMaxLevel] = useState(70);
  const [hideDefeated, setHideDefeated] = useState(false);
  const [checklistOpen, setChecklistOpen] = useState(false);
  const [share, setShare] = useState(null);
  const [copied, setCopied] = useState(false);

  const { data: custom = [] } = useQuery({ queryKey: ["markers"], queryFn: async () => (await api.get("/markers")).data });
  const { data: progress } = useQuery({ queryKey: ["progress"], queryFn: async () => (await api.get("/progress")).data });
  const defeatedSet = new Set(progress?.defeated || []);
  const { data: online } = useQuery({ queryKey: ["server-online"], queryFn: async () => (await api.get("/server/online")).data, refetchInterval: 30000 });

  const alphaId = (p) => `alpha-${p.idx}`;
  const toggleDefeated = async (p, defeated) => {
    const pal = PALS_BY_INDEX[p.idx];
    try {
      await api.post("/progress", { boss_id: alphaId(p), defeated, name: pal?.fr || "Alpha", type: "alpha", level: p.lvl });
      qc.invalidateQueries({ queryKey: ["progress"] });
      qc.invalidateQueries({ queryKey: ["leaderboard"] });
      qc.invalidateQueries({ queryKey: ["events"] });
      toast.success(defeated ? "Alpha marqué comme vaincu" : "Alpha réinitialisé");
    } catch { toast.error("Échec de la mise à jour"); }
  };
  const resetProgress = async () => {
    if (!window.confirm("Réinitialiser toute ta progression ? Tous les Alphas seront décochés.")) return;
    try {
      await api.delete("/progress");
      qc.invalidateQueries({ queryKey: ["progress"] });
      qc.invalidateQueries({ queryKey: ["leaderboard"] });
      toast.success("Progression réinitialisée — nouveau run !");
    } catch { toast.error("Échec de la réinitialisation"); }
  };

  const alphas = useMemo(() => pois.filter((p) => p.c === "alpha"), [pois]);
  const bossOrder = useMemo(() => [...alphas].sort((a, b) => (a.lvl || 0) - (b.lvl || 0)), [alphas]);
  const totalBosses = bossOrder.length;

  const visiblePois = useMemo(() => pois.filter((p) => {
    if (!activePOI.includes(p.c)) return false;
    if (p.c === "alpha") {
      if (p.lvl && p.lvl > maxLevel) return false;
      if (hideDefeated && defeatedSet.has(alphaId(p))) return false;
    }
    return true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [pois, activePOI, maxLevel, hideDefeated, progress]);

  const dotPois = useMemo(() => visiblePois.filter((p) => p.c !== "alpha" && p.c !== "sealed"), [visiblePois]);
  const palPois = useMemo(() => visiblePois.filter((p) => p.c === "alpha" || p.c === "sealed"), [visiblePois]);

  const customVisible = active.includes("custom") ? custom : [];

  const toggle = (t) => setActive((a) => (a.includes(t) ? a.filter((x) => x !== t) : [...a, t]));

  const searchResults = useMemo(() => {
    if (!query.trim()) return [];
    const q = query.toLowerCase();
    const pal = q.length >= 2
      ? PALS_BY_INDEX.filter((p) => p && (p.fr.toLowerCase().includes(q) || p.name.toLowerCase().includes(q)))
          .slice(0, 5).map((p) => ({ key: `pal-${p.i}`, pal: true, idx: p.i, name: p.fr, color: "#38BDF8", label: "Pal · fiche" }))
      : [];
    // Alpha bosses by pal name -> fly to their spot
    const alphaHits = alphas.filter((a) => { const pp = PALS_BY_INDEX[a.idx]; return pp && (pp.fr.toLowerCase().includes(q) || pp.name.toLowerCase().includes(q)); })
      .slice(0, 6).map((a) => { const pp = PALS_BY_INDEX[a.idx]; return { key: `alpha-${a.idx}`, name: `${pp.fr} (Alpha)`, x: a.x, y: a.y, color: "#EF4444", label: `Alpha · Nv. ${a.lvl}` }; });
    const cust = custom.filter((m) => m.name.toLowerCase().includes(q)).map((m) => ({
      key: `c-${m.id}`, name: m.name, x: m.x, y: m.y, color: (CUSTOM_TYPES[m.type] || CUSTOM_TYPES.base).color, label: (CUSTOM_TYPES[m.type] || CUSTOM_TYPES.base).label,
    }));
    const cats = Object.entries(POI_CATS).filter(([, meta]) => meta.label.toLowerCase().includes(q))
      .map(([k, meta]) => ({ key: `poi-${k}`, poiCat: k, name: meta.label, color: meta.color, label: `${pois.filter((p) => p.c === k).length} POI` }));
    return [...pal, ...cats, ...alphaHits, ...cust].slice(0, 12);
  }, [query, custom, pois, alphas]);

  const onPickResult = (r) => {
    if (r.pal) { openPal(r.idx); setQuery(""); return; }
    focusMarker(r);
  };
  const focusMarker = (m) => {
    if (m.poiCat) {
      setActivePOI((p) => (p.includes(m.poiCat) ? p : [...p, m.poiCat]));
      const pts = pois.filter((p) => p.c === m.poiCat);
      if (pts.length) {
        const cx = pts.reduce((s, p) => s + p.x, 0) / pts.length;
        const cy = pts.reduce((s, p) => s + p.y, 0) / pts.length;
        setFlyTarget({ x: cx, y: cy, zoom: 1, nonce: Date.now() });
      }
      setQuery(""); return;
    }
    setFlyTarget({ x: m.x, y: m.y, zoom: 4, nonce: Date.now() });
    setQuery("");
  };

  // Deep-link: /map?focus=poi:<cat> | alpha:<palIdx> | marker:<legacy>
  const location = useLocation();
  const [focusDone, setFocusDone] = useState("");
  useEffect(() => {
    const focus = new URLSearchParams(location.search).get("focus");
    if (!focus || focus === focusDone) return;
    if (focus.startsWith("alpha:")) {
      const idx = parseInt(focus.slice(6));
      const a = alphas.find((x) => x.idx === idx);
      if (a) { setActivePOI((p) => (p.includes("alpha") ? p : [...p, "alpha"])); setFlyTarget({ x: a.x, y: a.y, zoom: 4, nonce: Date.now() }); setFocusDone(focus); }
    } else if (focus.startsWith("poi:")) {
      focusMarker({ poiCat: focus.slice(4) }); setFocusDone(focus);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, alphas, focusDone]);

  const onMapClick = (latlng) => {
    const { x, y } = fromLL(latlng);
    setDraft({ x: Math.round(x), y: Math.round(y), name: "", type: "base", note: "" });
    setAddMode(false);
  };
  const saveDraft = async () => {
    if (!draft.name.trim()) { toast.error("Donne un nom à ton marqueur"); return; }
    try {
      await api.post("/markers", { name: draft.name, type: draft.type, x: draft.x, y: draft.y, note: draft.note });
      qc.invalidateQueries({ queryKey: ["markers"] });
      toast.success("Marqueur ajouté"); setDraft(null);
    } catch { toast.error("Échec de l'ajout du marqueur"); }
  };
  const removeMarker = async (id) => {
    try { await api.delete(`/markers/${id}`); qc.invalidateQueries({ queryKey: ["markers"] }); toast.success("Marqueur supprimé"); }
    catch { toast.error("Échec de la suppression"); }
  };
  const openShare = () => {
    if (!custom.length) { toast.error("Ajoute au moins un marqueur avant de partager"); return; }
    setShare({ step: "form", title: "Carte Palpagos" }); setCopied(false);
  };
  const generateShare = async () => {
    try {
      const { data } = await api.post("/share", { title: share.title?.trim() || "Carte Palpagos" });
      setShare({ step: "done", url: `${window.location.origin}/share/${data.id}`, count: data.count, title: data.title });
    } catch { toast.error("Échec de la création du lien de partage"); }
  };
  const copyShare = async () => {
    try { await navigator.clipboard.writeText(share.url); setCopied(true); toast.success("Lien copié !"); }
    catch { toast.error("Copie impossible — copie le lien manuellement"); }
  };

  return (
    <PalDetailProvider>
    <div className="fade-up relative rounded-lg overflow-hidden border border-border h-[calc(100vh-11rem)] md:h-[calc(100vh-7rem)]" data-testid="palpagos-map">
      <MapContainer
        crs={TILE_CRS}
        center={initView?.center || [NATIVE / 2, NATIVE / 2]}
        zoom={initView?.zoom ?? 1}
        maxBounds={BOUNDS}
        maxBoundsViscosity={1}
        minZoom={0}
        maxZoom={6}
        zoomControl={false}
        attributionControl={false}
        style={{ height: "100%", width: "100%", background: "#0a1a2e", cursor: addMode ? "crosshair" : "" }}
      >
        <ZoomControl position="bottomright" />
        <TileLayer url={TILE_URL} tileSize={TILE_SIZE} minNativeZoom={0} maxNativeZoom={MAX_NATIVE_ZOOM} noWrap bounds={BOUNDS} />
        <MapController flyTarget={flyTarget} addMode={addMode} onMapClick={onMapClick} onView={onView} />

        {/* Pal-face markers: alphas + sealed realms */}
        {markersVisible && palPois.map((p, i) => {
          const pal = PALS_BY_INDEX[p.idx];
          if (!pal) return null;
          const meta = POI_CATS[p.c];
          const isAlpha = p.c === "alpha";
          const defeated = isAlpha && defeatedSet.has(alphaId(p));
          return (
            <Marker key={`pal-${p.c}-${i}`} position={LL(p.x, p.y)} icon={palIcon(pal.img, isAlpha ? ringOf(pal) : meta.color, defeated)} zIndexOffset={500}>
              <Popup>
                <div className="min-w-[200px]">
                  <div className="flex items-center gap-2 mb-1">
                    <meta.icon className="w-4 h-4" style={{ color: meta.color }} />
                    <span className="font-head font-bold text-sm text-white">{pal.fr}</span>
                  </div>
                  <div className="text-[11px] uppercase tracking-wider mb-2" style={{ color: meta.color }}>
                    {meta.label}{p.lvl ? ` · Nv. ${p.lvl}` : ""}
                  </div>
                  <div className="mb-2"><ElementBadges elements={pal.elements} /></div>
                  {isAlpha && (
                    <button
                      data-testid={`toggle-defeated-${p.idx}`}
                      onClick={() => toggleDefeated(p, !defeated)}
                      className="mb-2 w-full inline-flex items-center justify-center gap-1.5 rounded-md px-2 py-1.5 text-xs font-semibold transition-colors"
                      style={defeated ? { background: "#22C55E22", border: "1px solid #22C55E", color: "#22C55E" } : { background: "transparent", border: "1px solid #334155", color: "#cbd5e1" }}
                    >
                      {defeated ? "✓ Vaincu — annuler" : "Marquer comme vaincu"}
                    </button>
                  )}
                  <button
                    data-testid={`map-pal-fiche-${p.idx}`}
                    onClick={() => openPal(p.idx)}
                    className="w-full inline-flex items-center justify-center gap-1.5 rounded-md px-2 py-1.5 text-xs font-semibold transition-colors"
                    style={{ background: "#38BDF818", border: "1px solid #38BDF8", color: "#38BDF8" }}
                  >
                    Voir la fiche du Pal
                  </button>
                </div>
              </Popup>
            </Marker>
          );
        })}

        {/* Dot POIs (fast travel, tower, dungeon, skill fruit, note) — clustered */}
        {markersVisible && dotPois.length > 0 && (
          <MarkerClusterGroup chunkedLoading maxClusterRadius={50} spiderfyOnMaxZoom showCoverageOnHover={false}>
            {dotPois.map((p, i) => {
              const key = poiKey(p);
              const isVisited = visited.has(key);
              const meta = POI_CATS[p.c];
              return (
                <Marker key={`dot-${i}`} position={LL(p.x, p.y)} icon={isVisited ? dotIconVisited() : dotIcon(meta.color)}>
                  <Popup>
                    <div className="min-w-[170px]">
                      <div className="flex items-center gap-2 mb-1">
                        <meta.icon className="w-4 h-4" style={{ color: meta.color }} />
                        <span className="font-head font-semibold text-sm text-white">{meta.label}</span>
                      </div>
                      <div className="text-[10px] text-slate-400 mb-2">Position : {Math.round(p.x)}, {Math.round(p.y)}</div>
                      <button
                        data-testid={`toggle-visited-${key}`}
                        onClick={() => toggleVisited(key)}
                        className="w-full inline-flex items-center justify-center gap-1.5 rounded-md px-2 py-1.5 text-xs font-semibold transition-colors"
                        style={isVisited ? { background: "#22C55E22", border: "1px solid #22C55E", color: "#22C55E" } : { background: "transparent", border: "1px solid #334155", color: "#cbd5e1" }}
                      >
                        {isVisited ? "✓ Visité — annuler" : "Marquer comme visité"}
                      </button>
                    </div>
                  </Popup>
                </Marker>
              );
            })}
          </MarkerClusterGroup>
        )}

        {/* Custom user markers */}
        {markersVisible && customVisible.map((m) => {
          const meta = CUSTOM_TYPES[m.type] || CUSTOM_TYPES.base;
          const Icon = meta.icon;
          const vkey = `custom:${m.id}`;
          const isVisited = visited.has(vkey);
          return (
            <Marker key={m.id} position={LL(m.x, m.y)} icon={customIcon(meta.color, meta.label[0], isVisited)}>
              <Popup>
                <div className="min-w-[190px]">
                  <div className="flex items-center gap-2 mb-1">
                    <Icon className="w-4 h-4" style={{ color: meta.color }} />
                    <span className="font-head font-bold text-sm text-white">{m.name}</span>
                  </div>
                  <div className="text-[11px] uppercase tracking-wider mb-2" style={{ color: meta.color }}>{meta.label} · perso</div>
                  {m.note && <p className="text-xs text-slate-300 leading-relaxed mb-2">{m.note}</p>}
                  <button data-testid={`toggle-visited-${vkey}`} onClick={() => toggleVisited(vkey)} className="w-full mb-2 inline-flex items-center justify-center gap-1.5 rounded-md px-2 py-1.5 text-xs font-semibold transition-colors" style={isVisited ? { background: "#22C55E22", border: "1px solid #22C55E", color: "#22C55E" } : { background: "transparent", border: "1px solid #334155", color: "#cbd5e1" }}>
                    {isVisited ? "✓ Visité — annuler" : "Marquer comme visité"}
                  </button>
                  <button data-testid={`delete-custom-marker-${m.id}`} onClick={() => removeMarker(m.id)} className="inline-flex items-center gap-1.5 text-xs font-semibold text-red-400 hover:text-red-300">
                    <Trash2 className="w-3.5 h-3.5" /> Supprimer
                  </button>
                </div>
              </Popup>
            </Marker>
          );
        })}
      </MapContainer>

      {/* Header + search */}
      <div className="absolute top-3 left-3 sm:top-4 sm:left-4 z-[500] w-[calc(100%-9rem)] max-w-[300px] sm:w-72 space-y-2">
        <div className="glass rounded-lg border border-border px-3 sm:px-4 py-1.5 sm:py-2">
          <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-primary/80 hidden sm:block">Carte interactive · 1.0</div>
          <div className="font-head font-bold text-base sm:text-lg leading-tight">Îles Palpagos</div>
        </div>
        <div className="glass rounded-lg border border-border relative">
          <div className="flex items-center gap-2 px-3 py-2">
            <Search className="w-4 h-4 text-muted-foreground shrink-0" />
            <input data-testid="map-search-input" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Rechercher…" className="flex-1 bg-transparent text-sm focus:outline-none min-w-0" />
            {query && <button data-testid="map-search-clear" onClick={() => setQuery("")} className="text-muted-foreground hover:text-foreground"><X className="w-4 h-4" /></button>}
          </div>
          {searchResults.length > 0 && (
            <div className="border-t border-border max-h-60 overflow-y-auto scrollbar-thin" data-testid="map-search-results">
              {searchResults.map((r) => (
                <button key={r.key} data-testid={`map-search-result-${r.key}`} onClick={() => onPickResult(r)} className="w-full flex items-center gap-2.5 px-3 py-2 text-left hover:bg-[hsl(var(--surface-hover))] transition-colors">
                  {r.pal ? <Info className="w-4 h-4 shrink-0" style={{ color: r.color }} /> : <MapPin className="w-4 h-4 shrink-0" style={{ color: r.color }} />}
                  <span className="flex-1 text-sm truncate">{r.name}</span>
                  <span className="text-[10px] uppercase tracking-wider text-muted-foreground">{r.label}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Top-right controls */}
      <div className="absolute top-3 right-3 sm:top-4 sm:right-4 z-[500] flex gap-1.5 sm:gap-2">
        <button data-testid="map-visibility-toggle" onClick={() => setMarkersVisible((v) => !v)} className={`glass rounded-md border p-2 sm:p-2.5 transition-colors ${markersVisible ? "border-border text-primary hover:border-primary/50" : "border-primary text-primary glow-primary"}`} title={markersVisible ? "Cacher les marqueurs" : "Afficher les marqueurs"}>
          {markersVisible ? <Eye className="w-4 h-4 sm:w-5 sm:h-5" /> : <EyeOff className="w-4 h-4 sm:w-5 sm:h-5" />}
        </button>
        <button data-testid="map-share-btn" onClick={openShare} className="glass rounded-md border border-border p-2 sm:p-2.5 hover:border-secondary/60 transition-colors" style={{ color: "#22C55E" }} title="Partager la carte">
          <Share2 className="w-4 h-4 sm:w-5 sm:h-5" />
        </button>
        <button data-testid="map-add-marker-toggle" onClick={() => { setAddMode((v) => !v); if (!addMode) toast.info("Clique (ou appui long) sur la carte pour placer ton marqueur"); }} className={`glass rounded-md border p-2 sm:p-2.5 transition-colors ${addMode ? "border-primary text-primary glow-primary" : "border-border hover:border-primary/50"}`} style={{ color: addMode ? undefined : "#22C55E" }} title="Ajouter un marqueur">
          <Plus className="w-4 h-4 sm:w-5 sm:h-5" />
        </button>
        <button data-testid="map-filter-toggle" onClick={() => setPanelOpen((o) => !o)} className={`glass rounded-md border p-2 sm:p-2.5 transition-colors ${panelOpen ? "border-primary glow-primary" : "border-border hover:border-primary/50"}`} title={panelOpen ? "Réduire les filtres" : "Afficher les filtres"}>
          <Filter className="w-4 h-4 sm:w-5 sm:h-5 text-primary" />
        </button>
      </div>

      {addMode && (
        <div className="absolute top-16 sm:top-4 left-1/2 -translate-x-1/2 z-[500] glass rounded-full border border-primary/50 px-4 py-1.5 text-xs sm:text-sm text-primary fade-up whitespace-nowrap">
          Clique sur la carte pour placer ton marqueur
        </div>
      )}

      {/* Legend / filter panel */}
      {panelOpen && markersVisible && (
        <div className="absolute top-[3.5rem] sm:top-16 right-3 sm:right-4 z-[500] glass rounded-lg border border-border p-3 sm:p-4 w-52 sm:w-60 max-h-[70vh] overflow-y-auto scrollbar-thin fade-up" data-testid="map-legend">
          <div className="flex items-center justify-between mb-3">
            <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-muted-foreground">Filtres</div>
            <button data-testid="map-legend-close" onClick={() => setPanelOpen(false)} className="text-muted-foreground hover:text-foreground -mr-1" title="Réduire"><X className="w-4 h-4" /></button>
          </div>

          {/* Online now */}
          <div className="mb-3 rounded-md border border-border bg-black/30 p-2.5" data-testid="online-widget">
            <div className="flex items-center justify-between text-xs mb-2">
              <span className="text-slate-300 font-semibold flex items-center gap-1.5"><span className={`w-2 h-2 rounded-full ${online?.online ? "bg-green-500 animate-pulse" : "bg-slate-500"}`} /> En ligne maintenant</span>
              <span className="font-mono" style={{ color: online?.online ? "#22C55E" : "#64748B" }} data-testid="online-count">{online?.online ? online.count : "—"}</span>
            </div>
            {online?.online ? (online.count > 0 ? (
              <div className="flex flex-col gap-1 max-h-32 overflow-y-auto scrollbar-thin">
                {online.players.map((p, i) => (
                  <div key={i} className="flex items-center gap-2 text-xs" data-testid={`online-player-${i}`}>
                    {p.picture ? <img src={p.picture} alt="" className="w-5 h-5 rounded-full object-cover shrink-0" /> : <div className="w-5 h-5 rounded-full bg-primary/15 border border-primary/30 flex items-center justify-center text-[9px] font-bold text-primary shrink-0">{(p.name || "?").charAt(0).toUpperCase()}</div>}
                    <span className="truncate text-slate-200">{p.name}</span>
                    {p.discord_username && <span className="text-[9px] shrink-0" style={{ color: "#5865F2" }}>@{p.discord_username}</span>}
                  </div>
                ))}
              </div>
            ) : <p className="text-[10px] text-muted-foreground">Personne en ligne pour l'instant.</p>) : <p className="text-[10px] text-muted-foreground">Serveur hors ligne.</p>}
          </div>

          {/* Alpha progression */}
          <div className="mb-3 rounded-md border border-border bg-black/30 p-2.5">
            <div className="flex items-center justify-between text-xs mb-2">
              <span className="text-slate-300 font-semibold">Alphas vaincus</span>
              <div className="flex items-center gap-2">
                <span className="font-mono" style={{ color: "#22C55E" }} data-testid="progress-count">{defeatedSet.size}/{totalBosses}</span>
                <button data-testid="reset-progress-btn" onClick={resetProgress} disabled={defeatedSet.size === 0} title="Réinitialiser" className="inline-flex items-center gap-1 rounded-md border border-border px-1.5 py-1 text-[10px] font-semibold text-muted-foreground hover:text-red-400 hover:border-red-400/50 transition-colors disabled:opacity-30 disabled:pointer-events-none">
                  <RotateCcw className="w-3 h-3" /> Reset
                </button>
              </div>
            </div>
            <div className="h-1.5 w-full rounded-full bg-black/50 overflow-hidden mb-2.5">
              <div className="h-full rounded-full transition-all duration-300" style={{ width: `${totalBosses ? (defeatedSet.size / totalBosses) * 100 : 0}%`, background: "#22C55E" }} />
            </div>
            <div className="flex items-center justify-between gap-2 mb-2.5" data-testid="map-rank">
              <RankBadge defeated={defeatedSet.size} size="sm" />
              {(() => { const nr = nextRank(defeatedSet.size); return nr ? <span className="text-[10px] text-muted-foreground">+{nr.min - defeatedSet.size} → {nr.label}</span> : <span className="text-[10px] font-semibold" style={{ color: "#FACC15" }}>Rang max !</span>; })()}
            </div>
            <div className="flex items-center justify-between text-[11px] mb-1">
              <span className="text-muted-foreground">Niveau max</span>
              <span className="font-mono text-primary">≤ {maxLevel}</span>
            </div>
            <input data-testid="level-filter" type="range" min="1" max="70" value={maxLevel} onChange={(e) => setMaxLevel(parseInt(e.target.value))} className="w-full accent-[#38BDF8] cursor-pointer" />
            <p className="text-[10px] text-muted-foreground mt-1 leading-tight">Baisse le curseur pour n'afficher que les Alphas de ton niveau.</p>

            <button data-testid="hide-defeated-toggle" onClick={() => setHideDefeated((v) => !v)} className={`mt-2.5 w-full flex items-center justify-between gap-2 rounded-md px-2.5 py-2 text-xs font-semibold transition-colors border ${hideDefeated ? "border-[#22C55E] text-[#22C55E] bg-[#22C55E14]" : "border-border text-slate-300 hover:bg-[hsl(var(--surface-hover))]"}`}>
              <span className="flex items-center gap-1.5">{hideDefeated ? <CheckCircle2 className="w-3.5 h-3.5" /> : <Circle className="w-3.5 h-3.5" />} Restants uniquement</span>
              <span className="text-[10px] text-muted-foreground">masque les vaincus</span>
            </button>

            <button data-testid="checklist-toggle" onClick={() => setChecklistOpen((v) => !v)} className="mt-1.5 w-full flex items-center gap-1.5 rounded-md px-2.5 py-2 text-xs font-semibold text-slate-300 hover:bg-[hsl(var(--surface-hover))] transition-colors">
              {checklistOpen ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
              <ListChecks className="w-3.5 h-3.5 text-primary" /> Ordre de progression
            </button>
            {checklistOpen && (
              <div className="mt-1.5 max-h-56 overflow-y-auto scrollbar-thin space-y-0.5" data-testid="boss-checklist">
                {bossOrder.map((b) => {
                  const pal = PALS_BY_INDEX[b.idx];
                  const done = defeatedSet.has(alphaId(b));
                  return (
                    <button key={b.idx} data-testid={`checklist-item-${b.idx}`} onClick={() => toggleDefeated(b, !done)} className="w-full flex items-center gap-2 rounded-md px-2 py-1.5 text-left hover:bg-[hsl(var(--surface-hover))] transition-colors">
                      {done ? <CheckCircle2 className="w-4 h-4 shrink-0" style={{ color: "#22C55E" }} /> : <Circle className="w-4 h-4 shrink-0 text-muted-foreground" />}
                      <span className="w-8 text-[10px] font-mono shrink-0" style={{ color: "#EF4444" }}>Nv{b.lvl}</span>
                      <span className={`flex-1 text-xs truncate ${done ? "line-through text-muted-foreground" : "text-slate-200"}`}>{pal?.fr || "Alpha"}</span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* POI category toggles */}
          <div className="space-y-1.5">
            {Object.entries(POI_CATS).map(([key, meta]) => {
              const Icon = meta.icon;
              const on = activePOI.includes(key);
              const count = pois.filter((p) => p.c === key).length;
              return (
                <button key={key} data-testid={`poi-filter-${key}`} onClick={() => togglePoi(key)} className={`w-full flex items-center gap-2.5 rounded-md px-2.5 py-2 text-sm transition-colors ${on ? "bg-[hsl(var(--surface-hover))]" : "opacity-45 hover:opacity-80"}`}>
                  <span className="w-6 h-6 rounded-full flex items-center justify-center shrink-0" style={{ background: `${meta.color}22`, border: `1px solid ${meta.color}`, color: meta.color }}><Icon className="w-3.5 h-3.5" /></span>
                  <span className="text-left flex-1">{meta.label}</span>
                  <span className="text-xs text-muted-foreground">{count}</span>
                </button>
              );
            })}
            <div className="h-px bg-border my-2" />
            <button data-testid="filter-custom" onClick={() => toggle("custom")} className={`w-full flex items-center gap-2.5 rounded-md px-2.5 py-2 text-sm transition-colors ${active.includes("custom") ? "bg-[hsl(var(--surface-hover))]" : "opacity-45 hover:opacity-80"}`}>
              <span className="w-6 h-6 rounded-full flex items-center justify-center shrink-0" style={{ background: "#38BDF822", border: "1px solid #38BDF8", color: "#38BDF8" }}><Flag className="w-3.5 h-3.5" /></span>
              <span className="text-left flex-1">Mes marqueurs</span>
              <span className="text-xs text-muted-foreground">{custom.length}</span>
            </button>
          </div>
        </div>
      )}

      {/* Add marker dialog */}
      {draft && (
        <div className="fixed inset-0 z-[1000] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={() => setDraft(null)} data-testid="add-marker-dialog">
          <div className="glass rounded-xl border border-border w-full max-w-sm p-6 fade-up relative" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setDraft(null)} className="absolute right-4 top-4 text-muted-foreground hover:text-foreground"><X className="w-5 h-5" /></button>
            <h3 className="font-head text-xl font-bold mb-4">Nouveau marqueur</h3>
            <label className="text-[10px] font-bold tracking-[0.2em] uppercase text-muted-foreground mb-1.5 block">Nom</label>
            <input data-testid="marker-name-input" autoFocus value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} placeholder="Ma base principale…" className="cfg-input mb-4" />
            <label className="text-[10px] font-bold tracking-[0.2em] uppercase text-muted-foreground mb-1.5 block">Type</label>
            <div className="grid grid-cols-3 gap-2 mb-4">
              {Object.entries(CUSTOM_TYPES).map(([key, meta]) => {
                const Icon = meta.icon;
                const on = draft.type === key;
                return (
                  <button key={key} data-testid={`marker-type-${key}`} onClick={() => setDraft({ ...draft, type: key })} className="flex flex-col items-center gap-1 rounded-md border py-2.5 text-xs font-semibold transition-colors" style={{ borderColor: on ? meta.color : "hsl(var(--border))", color: on ? meta.color : "hsl(var(--muted-foreground))", background: on ? `${meta.color}14` : "transparent" }}>
                    <Icon className="w-4 h-4" /> {meta.label}
                  </button>
                );
              })}
            </div>
            <label className="text-[10px] font-bold tracking-[0.2em] uppercase text-muted-foreground mb-1.5 block">Note (optionnel)</label>
            <textarea data-testid="marker-note-input" value={draft.note} onChange={(e) => setDraft({ ...draft, note: e.target.value })} rows={2} placeholder="Ex: mine de charbon + pals de feu" className="cfg-input mb-4 resize-none" />
            <button data-testid="marker-save-btn" onClick={saveDraft} className="w-full inline-flex items-center justify-center gap-2 rounded-md bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground hover:bg-primary/85 transition-colors">
              <Plus className="w-4 h-4" /> Ajouter le marqueur
            </button>
          </div>
        </div>
      )}

      {/* Share dialog */}
      {share && (
        <div className="fixed inset-0 z-[1000] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={() => setShare(null)} data-testid="share-dialog">
          <div className="glass rounded-xl border border-border w-full max-w-md p-6 fade-up relative" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setShare(null)} className="absolute right-4 top-4 text-muted-foreground hover:text-foreground"><X className="w-5 h-5" /></button>
            <div className="flex items-center gap-2 mb-2"><Share2 className="w-5 h-5" style={{ color: "#22C55E" }} /><h3 className="font-head text-xl font-bold">Partager ta carte</h3></div>
            {share.step === "form" ? (
              <>
                <p className="text-sm text-muted-foreground mb-4">Donne un nom à ta carte avant de générer le lien.</p>
                <label className="text-[10px] font-bold tracking-[0.2em] uppercase text-muted-foreground mb-1.5 block">Titre de la carte</label>
                <input data-testid="share-title-input" autoFocus value={share.title} onChange={(e) => setShare({ ...share, title: e.target.value })} placeholder='Ex: "Spots de farm guilde"' className="cfg-input mb-4" />
                <button data-testid="share-generate-btn" onClick={generateShare} className="w-full inline-flex items-center justify-center gap-2 rounded-md bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground hover:bg-primary/85 transition-colors">
                  <Share2 className="w-4 h-4" /> Générer le lien
                </button>
              </>
            ) : (
              <>
                <p className="text-sm text-muted-foreground mb-4">« <span className="text-foreground font-semibold">{share.title}</span> » — lien en lecture seule avec tes {share.count} marqueur(s).</p>
                <div className="flex items-center gap-2 rounded-md bg-black/40 border border-border px-3 py-2 mb-3">
                  <input data-testid="share-url-input" readOnly value={share.url} className="flex-1 bg-transparent font-mono text-xs focus:outline-none" onFocus={(e) => e.target.select()} />
                  <button data-testid="share-copy-btn" onClick={copyShare} className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground hover:bg-primary/85 transition-colors shrink-0">
                    {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />} {copied ? "Copié" : "Copier"}
                  </button>
                </div>
                <a data-testid="share-open-link" href={share.url} target="_blank" rel="noopener noreferrer" className="text-xs text-primary hover:underline">Ouvrir dans un nouvel onglet →</a>
              </>
            )}
          </div>
        </div>
      )}

      {/* Collapsible legend */}
      {markersVisible && (
        <div className="absolute bottom-3 left-3 z-[500]" data-testid="map-legend-wrapper">
          {legendOpen && (
            <div className="mb-2 glass rounded-lg border border-border p-3 w-56 max-h-[55vh] overflow-y-auto scrollbar-thin fade-up" data-testid="legend-panel">
              <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-muted-foreground mb-1.5">Légende</div>
              <div className="space-y-1">
                {Object.values(POI_CATS).map((m, i) => (
                  <div key={i} className="flex items-center gap-2 text-xs">
                    <span className="w-3 h-3 rounded-full shrink-0" style={{ background: m.color, border: "2px solid rgba(0,0,0,.4)" }} />
                    <span className="text-slate-200">{m.label}</span>
                  </div>
                ))}
                <div className="flex items-center gap-2 text-xs">
                  <span className="w-3 h-3 rounded-full shrink-0" style={{ background: "#38BDF833", border: "2px solid #38BDF8" }} />
                  <span className="text-slate-200">Mes marqueurs</span>
                </div>
              </div>
            </div>
          )}
          <button data-testid="legend-toggle" onClick={() => setLegendOpen((v) => !v)} className="glass rounded-lg border border-border px-3 py-2 text-xs font-semibold flex items-center gap-1.5 hover:bg-[hsl(var(--surface-hover))] transition-colors">
            <Info className="w-3.5 h-3.5 text-primary" /> Légende
            {legendOpen ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
          </button>
        </div>
      )}
    </div>
    </PalDetailProvider>
  );
}
